import { useEffect, useState } from "react";
import axios from "axios";
import { Waves, Ship, Droplets, Gauge, Thermometer, ChevronDown, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ratingColor(score, isDarkMode) {
  if (score >= 8) return isDarkMode ? "text-cyan-200" : "text-cyan-800";
  if (score >= 6) return isDarkMode ? "text-blue-200" : "text-blue-800";
  if (score >= 4) return isDarkMode ? "text-amber-200" : "text-amber-800";
  return isDarkMode ? "text-red-200" : "text-red-700";
}

export function LakeRiverSportWidget({ lat, lon, isDarkMode }) {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!lat || !lon) return;
    let isMounted = true;
    axios
      .get(`${API}/weather/sports/lake-river`, { params: { lat, lon }, timeout: 14000 })
      .then((response) => { if (isMounted) setData(response.data); })
      .catch((error) => { console.error("Lake/River sport widget error:", error.message); });
    return () => { isMounted = false; };
  }, [lat, lon]);

  const panel = isDarkMode ? "bg-white/5 border-white/10" : "bg-white/80 border-gray-200";
  const muted = isDarkMode ? "text-white/55" : "text-gray-600";

  if (!data?.success) {
    return (
      <div data-testid="lake-river-widget-loading">
        <div className="flex items-center gap-2 mb-3">
          <Waves className={`w-5 h-5 ${isDarkMode ? "text-blue-300" : "text-blue-700"}`} />
          <h3 className={`font-semibold ${isDarkMode ? "text-white/80" : "text-gray-800"}`}>River Conditions</h3>
        </div>
        <div className="animate-pulse grid grid-cols-2 gap-2">
          <div className={`h-16 rounded-xl ${isDarkMode ? "bg-white/10" : "bg-gray-200"}`} />
          <div className={`h-16 rounded-xl ${isDarkMode ? "bg-white/10" : "bg-gray-200"}`} />
        </div>
        <div className={`animate-pulse h-20 rounded-xl mt-3 ${isDarkMode ? "bg-white/10" : "bg-gray-200"}`} />
        <div className="animate-pulse grid grid-cols-2 gap-2 mt-3" data-testid="river-visible-detail-grid">
          <div className={`h-24 rounded-xl ${isDarkMode ? "bg-white/10" : "bg-gray-200"}`} />
          <div className={`h-24 rounded-xl ${isDarkMode ? "bg-white/10" : "bg-gray-200"}`} />
        </div>
        <div className="animate-pulse grid grid-cols-3 gap-2 mt-3" data-testid="river-visible-metrics-grid">
          <div className={`h-16 rounded-xl ${isDarkMode ? "bg-white/10" : "bg-gray-200"}`} />
          <div className={`h-16 rounded-xl ${isDarkMode ? "bg-white/10" : "bg-gray-200"}`} />
          <div className={`h-16 rounded-xl ${isDarkMode ? "bg-white/10" : "bg-gray-200"}`} />
        </div>
      </div>
    );
  }

  const lake = data.lake || {};
  const river = data.river || {};
  const station = river.primary_station;

  return (
    <div data-testid="lake-river-sport-widget" className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Waves className={`w-5 h-5 shrink-0 ${isDarkMode ? "text-blue-300" : "text-blue-700"}`} />
          <div className="min-w-0">
            <h3 className={`font-semibold ${isDarkMode ? "text-white/85" : "text-gray-900"}`}>River Conditions</h3>
            <p className={`text-xs truncate ${muted}`} data-testid="lake-river-source-label">{data.source}</p>
          </div>
        </div>
        <div className="text-right shrink-0" data-testid="lake-river-boating-score">
          <p className={`text-3xl font-black leading-none ${ratingColor(lake.boating_index, isDarkMode)}`}>{lake.boating_index}</p>
          <p className={`text-[11px] font-semibold ${muted}`}>Boating</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className={`rounded-xl border p-3 ${panel}`} data-testid="lake-surface-condition">
          <div className="flex items-center gap-1.5 mb-1"><Ship className="w-4 h-4 text-blue-400" /><span className={`text-xs ${muted}`}>Lake surface</span></div>
          <p className="text-lg font-bold">{lake.surface}</p>
          <p className={`text-[11px] ${muted}`}>{lake.estimated_wave_inches} in estimated chop</p>
        </div>
        <div className={`rounded-xl border p-3 ${panel}`} data-testid="river-flow-condition">
          <div className="flex items-center gap-1.5 mb-1"><Droplets className="w-4 h-4 text-cyan-400" /><span className={`text-xs ${muted}`}>River flow</span></div>
          <p className="text-lg font-bold">{station?.flow_status || "Unreported"}</p>
          <p className={`text-[11px] ${muted}`}>{river.safety_note}</p>
        </div>
      </div>

      <div className={`rounded-xl border p-3 ${panel}`} data-testid="lake-river-primary-summary">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className={`text-xs font-semibold mb-1 ${isDarkMode ? "text-blue-200" : "text-blue-800"}`}>Sport water read</p>
            <p className={`text-sm leading-relaxed ${muted}`}>Boat: {lake.boating_rating} · River: {river.rating} · {lake.water_temp_f ? `Water ${lake.water_temp_f}°F` : "Water temp unavailable"}</p>
          </div>
          <div className={`text-xl font-black ${ratingColor(river.index, isDarkMode)}`} data-testid="river-condition-score">{river.index}/10</div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="river-visible-detail-grid">
        <div className={`rounded-xl border p-3 ${panel}`} data-testid="river-visible-station-card">
          <div className="flex items-center gap-1.5 mb-1"><Gauge className="w-4 h-4 text-cyan-400" /><span className="text-xs font-semibold">Primary station</span></div>
          <p className="text-sm font-bold truncate">{station?.name || "No nearby live flow station"}</p>
          <p className={`text-[11px] ${muted}`}>{station ? `${station.distance_mi} mi · ${station.discharge_cfs ? `${station.discharge_cfs} cfs` : "flow not reported"}` : "Using weather-derived river safety read"}</p>
        </div>
        <div className={`rounded-xl border p-3 ${panel}`} data-testid="river-visible-quality-card">
          <div className="flex items-center gap-1.5 mb-1"><Thermometer className="w-4 h-4 text-amber-400" /><span className="text-xs font-semibold">Water quality</span></div>
          <p className="text-sm font-bold">{station?.water_quality || "Limited sensors"}</p>
          <p className={`text-[11px] ${muted}`}>{data.weather?.visibility_mi ?? "—"} mi visibility · {data.weather?.description}</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2" data-testid="river-visible-metrics-grid">
        <div className={`rounded-xl border p-3 text-center ${panel}`} data-testid="river-visible-stage-metric">
          <p className={`text-[11px] ${muted}`}>Stage</p>
          <p className="text-sm font-bold">{station?.gage_height_ft ? `${station.gage_height_ft} ft` : "—"}</p>
        </div>
        <div className={`rounded-xl border p-3 text-center ${panel}`} data-testid="river-visible-temp-metric">
          <p className={`text-[11px] ${muted}`}>Temp</p>
          <p className="text-sm font-bold">{station?.water_temp_f ? `${station.water_temp_f}°F` : (lake.water_temp_f ? `${lake.water_temp_f}°F` : "—")}</p>
        </div>
        <div className={`rounded-xl border p-3 text-center ${panel}`} data-testid="river-visible-wading-metric">
          <p className={`text-[11px] ${muted}`}>Wading</p>
          <p className="text-sm font-bold truncate">{station?.wadeability || river.safety_note || "Check locally"}</p>
        </div>
      </div>

      <button
        type="button"
        onClick={(event) => { event.stopPropagation(); setExpanded((value) => !value); }}
        className={`w-full flex items-center justify-center gap-2 rounded-xl border px-3 py-2 text-xs font-bold transition-colors ${isDarkMode ? "border-blue-400/20 bg-blue-400/10 text-blue-200 hover:bg-blue-400/20" : "border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100"}`}
        data-testid="lake-river-widget-expand-button"
        aria-expanded={expanded}
      >
        {expanded ? "Hide river details" : "Show river details"}
        <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
      </button>

      {expanded && (
        <div className="space-y-3" data-testid="lake-river-widget-expanded-panel">
          {lake.warnings?.length > 0 && (
            <div className={`rounded-xl border p-3 ${isDarkMode ? "bg-amber-500/10 border-amber-400/20" : "bg-amber-50 border-amber-200"}`} data-testid="lake-river-warnings-panel">
              <div className="flex items-center gap-1.5 mb-1"><AlertTriangle className="w-4 h-4 text-amber-400" /><span className="text-xs font-semibold">Water warnings</span></div>
              {lake.warnings.map((warning) => <p key={warning} className={`text-xs ${muted}`}>{warning}</p>)}
            </div>
          )}
          {station && (
            <div className={`rounded-xl border p-3 ${panel}`} data-testid="lake-river-primary-station">
              <div className="flex items-center gap-1.5 mb-1"><Gauge className="w-4 h-4 text-cyan-400" /><span className="text-xs font-semibold">Primary river station</span></div>
              <p className="text-sm font-bold truncate">{station.name}</p>
              <div className={`grid grid-cols-2 gap-2 mt-2 text-xs ${muted}`}>
                <span data-testid="lake-river-discharge-value">Flow: {station.discharge_cfs ? `${station.discharge_cfs} cfs` : "—"}</span>
                <span data-testid="lake-river-stage-value">Stage: {station.gage_height_ft ? `${station.gage_height_ft} ft` : "—"}</span>
                <span data-testid="lake-river-temp-value">Temp: {station.water_temp_f ? `${station.water_temp_f}°F` : "—"}</span>
                <span data-testid="lake-river-quality-value">Quality: {station.water_quality}</span>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            {river.stations?.slice(0, 4).map((row) => (
              <div key={row.site_code} className={`rounded-xl border p-3 ${panel}`} data-testid={`lake-river-station-${row.site_code}`}>
                <p className="text-xs font-bold truncate">{row.name}</p>
                <p className={`text-[11px] ${muted}`}>{row.distance_mi} mi · {row.flow_status}</p>
              </div>
            ))}
          </div>
          <div className={`rounded-xl border p-3 ${panel}`} data-testid="lake-river-weather-context">
            <div className="flex items-center gap-1.5 mb-1"><Thermometer className="w-4 h-4 text-amber-400" /><span className="text-xs font-semibold">Weather context</span></div>
            <p className={`text-xs ${muted}`}>{data.weather?.wind_speed_mph ?? "—"} mph {data.weather?.wind_direction} wind · {data.weather?.visibility_mi ?? "—"} mi visibility · {data.weather?.description}</p>
          </div>
        </div>
      )}
    </div>
  );
}